import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import api from '../services/api';

const GoogleMapsContext = createContext({
  isLoaded: false,
  loadError: null,
});

const libraries = ['places', 'geometry', 'drawing'];

const GoogleMapsScriptLoader = ({ apiKey, configError, children }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  return (
    <GoogleMapsContext.Provider value={{ isLoaded, loadError: configError || loadError }}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

export const GoogleMapsProvider = ({ children }) => {
  const [mapsApiKey, setMapsApiKey] = useState('');
  const [configLoaded, setConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadPublicConfig = async () => {
      try {
        const config = await api.get('/config/public', { skipAuthHandling: true });
        const key = config?.google_maps_api_key || '';

        if (!key) {
          throw new Error('Google Maps browser API key is not configured');
        }

        if (isMounted) {
          setMapsApiKey(key);
          setConfigError(null);
        }
      } catch (error) {
        if (isMounted) {
          setConfigError(error);
        }
      } finally {
        if (isMounted) {
          setConfigLoaded(true);
        }
      }
    };

    loadPublicConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  const loadingContext = useMemo(
    () => ({
      isLoaded: false,
      loadError: configError,
    }),
    [configError]
  );

  if (!configLoaded || !mapsApiKey) {
    return <GoogleMapsContext.Provider value={loadingContext}>{children}</GoogleMapsContext.Provider>;
  }

  return (
    <GoogleMapsScriptLoader apiKey={mapsApiKey} configError={configError}>
      {children}
    </GoogleMapsScriptLoader>
  );
};

export const useGoogleMaps = () => {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    throw new Error('useGoogleMaps must be used within GoogleMapsProvider');
  }
  return context;
};
